import type { MaskedTextBoxEvent } from "@tql/ui-components-react/kendo/inputs";
import {
	Input,
	NumericTextBox,
	RadioGroup,
	TextArea,
} from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import { MaskedTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { parseISO } from "date-fns";
import { useCallback, useState } from "react";
import { useParams } from "react-router";
import { z } from "zod";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import type { AugmentedShipment } from "../../types/augmented-shipment";
import * as styles from "./claims-modal.module.css";
import { useClaimsFieldIds } from "./hooks/use-claims-field-ids.ts";
import { useGetClaimReasons } from "./hooks/use-get-claim-reasons.ts";
import { usePostNewClaim } from "./hooks/use-post-new-claim.ts";
import { initFormState, yesNoOptions } from "./utils/constants.ts";

const emailSchema = z.string().email();

const initialErrors = {
	claimReason: false,
	salvageAmount: false,
	estimatedClaimAmount: false,
	actualClaimAmount: false,
	contactEmail: false,
};

export const ClaimsModal = ({
	onClose,
	isOpen,
	shipment,
}: {
	onClose: () => void;
	isOpen: boolean;
	shipment: AugmentedShipment;
}) => {
	const params = useParams();
	const ids = useClaimsFieldIds();
	const { postNewClaimAsync, isPostingNewClaim } = usePostNewClaim();
	const { claimReasons, isLoading: isLoadingClaimReasons } =
		useGetClaimReasons();

	const claimReasonErrorId = `${ids.claimReason}_error`;
	const salvageAmountErrorId = `${ids.salvageAmount}_error`;
	const estimatedClaimAmountErrorId = `${ids.estimatedClaimAmount}_error`;
	const actualClaimAmountErrorId = `${ids.actualClaimAmount}_error`;
	const contactEmailErrorId = `${ids.contactEmail}_error`;

	const buildInitialFormData = useCallback(
		() => ({
			poNumber: params["poNumber"] ?? "",
			customerName: shipment?.customer?.customerName ?? "",
			carrier: shipment?.carrier,
			loadDate: shipment?.shipmentDetails?.pickup?.actual,
			deliveryDate: shipment?.shipmentDetails?.delivery?.actual,
			...initFormState,
		}),
		[params, shipment],
	);

	const [errors, setErrors] = useState(initialErrors);
	const [formData, setFormData] = useState(buildInitialFormData);

	const handleChange = (name: string, value: string | number) => {
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		if (name in errors) {
			setErrors((prev) => ({
				...prev,
				[name]: false,
			}));
		}
	};

	const resetForm = () => {
		setFormData(buildInitialFormData());
		setErrors(initialErrors);
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const handleConfirm = async () => {
		const nextErrors = {
			claimReason: !formData.claimReason,
			salvageAmount: formData.salvageAmount < 0,
			estimatedClaimAmount: formData.estimatedClaimAmount < 0,
			actualClaimAmount: formData.actualClaimAmount < 0,
			contactEmail: !emailSchema.safeParse(formData.contactEmail.trim())
				.success,
		};

		if (Object.values(nextErrors).some(Boolean)) {
			setErrors(nextErrors);
			return;
		}

		try {
			await postNewClaimAsync(formData);
		} catch {
			return;
		}

		resetForm();
		onClose();
	};

	if (!isOpen) {
		return null;
	}

	return (
		<TraxModal
			confirmDisabled={isPostingNewClaim}
			onCancel={handleClose}
			onClose={handleClose}
			onConfirm={() => void handleConfirm()}
			title={"Claim Information"}
			type={"confirmation"}
			width={"50%"}
		>
			<LoadingSpinner isVisible={isPostingNewClaim} />
			<div className={styles.row}>
				<div>
					<Label className={styles.loadInfoLabel}>Load Information</Label>
					<div className={styles.flex}>
						<div className={styles.field}>
							<Label editorId={ids.poNumber}>PO Number</Label>
							<Input
								disabled={true}
								id={ids.poNumber}
								value={formData.poNumber}
							/>
						</div>
						<div className={styles.field}>
							<Label editorId={ids.loadDate}>Load Date</Label>
							{formData.loadDate ? (
								<DatePicker
									disabled={true}
									id={ids.loadDate}
									value={parseISO(formData.loadDate)}
								/>
							) : (
								<div>N/A</div>
							)}
						</div>
						<div className={styles.field}>
							<Label editorId={ids.deliveryDate}>Delivery Date</Label>
							{formData.deliveryDate ? (
								<DatePicker
									disabled={true}
									id={ids.deliveryDate}
									value={parseISO(formData.deliveryDate)}
								/>
							) : (
								<div>N/A</div>
							)}
						</div>
					</div>

					<Label className={styles.sectionLabel}>Customer Information</Label>
					<div className={styles.flex}>{formData.customerName || "N/A"}</div>

					<Label className={styles.sectionLabel}>Carrier Information</Label>
					<div className={styles.flex}>
						{formData.carrier?.carrierName || "N/A"}
					</div>

					<Label className={styles.sectionLabel}>Claim Information</Label>
					<div className={styles.flex}>
						<div>
							NOTE: The claims contact is often different than your
							sales/transportation contact with your customer.
						</div>
					</div>
					<div className={`${styles.row} ${styles.spacedTop}`}>
						<div>
							<Label id={`${ids.needHelpWithSalvage}_label`}>
								I need help with salvage.
							</Label>
							<RadioGroup
								ariaLabelledBy={`${ids.needHelpWithSalvage}_label`}
								data={yesNoOptions}
								layout={"horizontal"}
								name={"needHelpWithSalvage"}
								onChange={(event: { value: string }) =>
									handleChange("needHelpWithSalvage", event.value)
								}
								value={formData.needHelpWithSalvage}
							/>
						</div>
						<div>
							<Label editorId={ids.salvageAmount}>Salvage Amount</Label>
							<NumericTextBox
								ariaDescribedBy={
									errors.salvageAmount ? salvageAmountErrorId : undefined
								}
								id={ids.salvageAmount}
								min={0}
								onChange={({ value }: { value: number | null }) =>
									handleChange("salvageAmount", value ?? 0)
								}
								spinners={false}
								valid={!errors.salvageAmount}
								value={formData.salvageAmount}
							/>
							{errors.salvageAmount ? (
								<Error id={salvageAmountErrorId}>
									Salvage amount must be at least 0.
								</Error>
							) : null}
						</div>
					</div>
					<div className={`${styles.row} ${styles.spacedTop}`}>
						<div>
							<Label editorId={ids.estimatedClaimAmount}>
								Estimated Claim Amount
							</Label>

							<NumericTextBox
								ariaDescribedBy={
									errors.estimatedClaimAmount
										? estimatedClaimAmountErrorId
										: undefined
								}
								id={ids.estimatedClaimAmount}
								min={0}
								onChange={({ value }: { value: number | null }) =>
									handleChange("estimatedClaimAmount", value ?? 0)
								}
								spinners={false}
								valid={!errors.estimatedClaimAmount}
								value={formData.estimatedClaimAmount}
							/>
							{errors.estimatedClaimAmount ? (
								<Error id={estimatedClaimAmountErrorId}>
									Estimated claim amount must be at least 0.
								</Error>
							) : null}
						</div>
						<div>
							<Label editorId={ids.actualClaimAmount}>
								Actual Claim Amount
							</Label>
							<NumericTextBox
								ariaDescribedBy={
									errors.actualClaimAmount
										? actualClaimAmountErrorId
										: undefined
								}
								id={ids.actualClaimAmount}
								min={0}
								onChange={({ value }: { value: number | null }) =>
									handleChange("actualClaimAmount", value ?? 0)
								}
								spinners={false}
								valid={!errors.actualClaimAmount}
								value={formData.actualClaimAmount}
							/>
							{errors.actualClaimAmount ? (
								<Error id={actualClaimAmountErrorId}>
									Actual claim amount must be at least 0.
								</Error>
							) : null}
						</div>
					</div>

					<div>
						<div>
							<Label editorId={ids.claimReason}>
								Claim Reason
								<span className={"required"}>&nbsp;*</span>
							</Label>

							<DropDownList
								ariaDescribedBy={
									errors.claimReason ? claimReasonErrorId : undefined
								}
								data={claimReasons}
								dataItemKey={"id"}
								id={ids.claimReason}
								loading={isLoadingClaimReasons}
								onChange={(e: { value: { id: string } }) =>
									handleChange("claimReason", e.value?.id)
								}
								placeholder={"Claim Reason"}
								textField={"name"}
								valid={!errors.claimReason}
								value={
									claimReasons.find(
										(reason) => reason.id === formData.claimReason,
									) ?? null
								}
							/>
							{errors.claimReason ? (
								<Error id={claimReasonErrorId}>Claim reason is required.</Error>
							) : null}
						</div>
					</div>

					<div className={`${styles.row} ${styles.spacedTop}`}>
						<div>
							<Label id={`${ids.tempRecorder}_label`}>
								Is there a temp recorder available?
							</Label>

							<RadioGroup
								ariaLabelledBy={`${ids.tempRecorder}_label`}
								data={yesNoOptions}
								layout={"horizontal"}
								name={"tempRecorder"}
								onChange={(e: { value: string }) =>
									handleChange("tempRecorder", e.value)
								}
								value={formData.tempRecorder}
							/>
						</div>
						<div>
							<Label id={`${ids.fedInspection}_label`}>
								If this is a temperature issue produce load, has a Federal
								Inspection be completed?
							</Label>
							<RadioGroup
								ariaLabelledBy={`${ids.fedInspection}_label`}
								data={yesNoOptions}
								layout={"horizontal"}
								name={"fedInspection"}
								onChange={(e: { value: string }) =>
									handleChange("fedInspection", e.value)
								}
								value={formData.fedInspection}
							/>
						</div>
					</div>
				</div>

				<div>
					<div>
						<Label>Administration</Label>
						<div id={`${ids.claimExceedPayAmount}_label`}>
							Does the estimated value of the claim exceed the pay amount to the
							truck (less any comchecks given)?
						</div>
						<div>
							<RadioGroup
								ariaLabelledBy={`${ids.claimExceedPayAmount}_label`}
								data={yesNoOptions}
								layout={"horizontal"}
								name={"claimExceedPayAmount"}
								onChange={(e: { value: string }) =>
									handleChange("claimExceedPayAmount", e.value)
								}
								value={formData.claimExceedPayAmount}
							/>
						</div>
					</div>

					<div>
						<Label className={styles.sectionLabel}>
							Customer Claims Contact Information
						</Label>
						<div>
							NOTE: The claims contact is often different than your
							sales/transportation contact with your customer.
						</div>
					</div>

					<div className={styles.spacedTop}>
						<Label editorId={ids.contactName}>Contact Name</Label>
						<div>
							<Input
								id={ids.contactName}
								name={"contactName"}
								onChange={(e: { value: string }) =>
									handleChange("contactName", e.value)
								}
								value={formData.contactName}
							/>
						</div>
					</div>

					<div className={styles.spacedTop}>
						<Label editorId={ids.contactEmail}>Email</Label>
						<span className={"required"}>&nbsp;*</span>
						<div>
							<Input
								ariaDescribedBy={
									errors.contactEmail ? contactEmailErrorId : undefined
								}
								id={ids.contactEmail}
								name={"contactEmail"}
								onChange={(e: { value: string }) =>
									handleChange("contactEmail", e.value)
								}
								type={"email"}
								valid={!errors.contactEmail}
								value={formData.contactEmail}
							/>
							{errors.contactEmail ? (
								<Error id={contactEmailErrorId}>
									Enter a valid email address.
								</Error>
							) : null}
						</div>
					</div>

					<div className={`${styles.row} ${styles.spacedTop}`}>
						<div className={styles.flexGrow2}>
							<Label editorId={ids.contactPhone}>Phone</Label>
							<MaskedTextBox
								id={ids.contactPhone}
								mask={"(000) 000-0000"}
								name={"contactPhone"}
								onChange={(e: MaskedTextBoxEvent) =>
									handleChange("contactPhone", e.target.value)
								}
								placeholder={"(___) ___-____"}
								value={formData.contactPhone}
							/>
						</div>
						<div className={styles.flexGrow1}>
							<Label editorId={ids.contactExtension}>EXT</Label>
							<Input
								className={styles.fullWidth}
								id={ids.contactExtension}
								name={"contactExtension"}
								onChange={(e: { value: string }) =>
									handleChange("contactExtension", e.value)
								}
								value={formData.contactExtension}
							/>
						</div>
					</div>

					<div className={styles.spacedTop}>
						<Label editorId={ids.claimDetails}>
							Claims Details & Description
						</Label>
						<div>
							<TextArea
								id={ids.claimDetails}
								name={"claimDetails"}
								onChange={(e: { value: string }) =>
									handleChange("claimDetails", e.value)
								}
								rows={5}
								value={formData.claimDetails}
							/>
						</div>
					</div>
				</div>
			</div>
		</TraxModal>
	);
};
